import {JourneyModel} from "../../journey/journey.model";
import {IBundleSsrUpgradeModel, IBundleSsrUpgradePair} from "../../bundle/bundle-ssr-upgrade.model.interface";
import {
    IDotRezSellJourneyBundleRequest
} from "../../../../dot-rez-api/data-contracts/requests/booking/sell-bundle.request";
import {BundleSsrUpgradeNote} from "../../bundle/bundle-ssr-upgrade-note";
import {Check} from "../../../../../types/type-checking";
import {
    isChildBundleCode
} from "../../../../bundle-types-configuration/bundle-types-configuration.service.interface";

function buildBundleSsrsUpgradeRequest(bundleSsrsUpgrades: IBundleSsrUpgradeModel[]): IDotRezSellJourneyBundleRequest["upgradeRequest"] | undefined {
    if(bundleSsrsUpgrades.length === 0) {
        return undefined;
    }

    return {
        keys: bundleSsrsUpgrades.selectMany(ssrUpgrade => {
            const upgradeKeys = ssrUpgrade.getUpgradeSsrKeys();
            const upgradeNote = new BundleSsrUpgradeNote(upgradeKeys.fromSsrType);
            return upgradeKeys.ssrKeys.map(ssrKey => {
                return {
                    ssrKey: ssrKey,
                    count: 1,
                    note: upgradeNote.toString()
                }
            })
        })
    }
}

export function createJourneySellBundleRequests(bundleCodeToSell: string, journey: JourneyModel, bundleSsrsUpgradesPairs: IBundleSsrUpgradePair[])
    : {requests: IDotRezSellJourneyBundleRequest[]; ssrKeysToRemove: string[];} {

    const bundle = journey.bundlesAvailability.find(b => b.bundleCode === bundleCodeToSell);
    if(!bundle) {
        throw new Error(`There is no available bundle with code ${bundleCodeToSell} for journey ${journey.journeyKey}`);
    }


    const sellJourneyBundleRequests: IDotRezSellJourneyBundleRequest[] = [];

    const adultPassengersKeys = journey.booking.passengers.filter(p => p.passengerType.isAdult).map(p => p.passengerKey);
    let childPassengersKeys = journey.booking.passengers.filter(p => p.passengerType.isChild).map(p => p.passengerKey);

    sellJourneyBundleRequests.push({
        journeyKey: journey.journeyKey,
        bundleCode: bundle.bundleCode,
        passengerKeys: adultPassengersKeys
    });



    if(journey.childBundle && childPassengersKeys.length > 0 && !journey.booking.pso.isPsoAcceptedByUser) { // we sell child bundle only if the user didn't explicitly select PSO

        childPassengersKeys = childPassengersKeys.filter(childKey => journey.getPassengerBundle(childKey)?.bundleCode !== journey.childBundle?.bundleCode);

        if(childPassengersKeys.length > 0) {
            sellJourneyBundleRequests.push({
                journeyKey: journey.journeyKey,
                bundleCode: journey.childBundle.bundleCode,
                passengerKeys: childPassengersKeys
            })
        }


    } else {
        sellJourneyBundleRequests[0].passengerKeys = [
            ...adultPassengersKeys,
            ...childPassengersKeys
        ]
    }

    const journeyBundleSsrUpgrades = bundle.getSsrsAvailableUpgrades(bundleSsrsUpgradesPairs);
    const upgradeBundleSsrsRequest = buildBundleSsrsUpgradeRequest(journeyBundleSsrUpgrades);
    if(upgradeBundleSsrsRequest) {
        sellJourneyBundleRequests[0].upgradeRequest = upgradeBundleSsrsRequest;
    }

    let ssrKeysToRemoveOnBundleChange = [
        ...journey.getSsrKeysToRemoveOnBundleChange(bundle, journeyBundleSsrUpgrades),
        ...journeyBundleSsrUpgrades.selectMany(ssrUpgrade => ssrUpgrade.getSsrKeysToRemoveWhenUpgrading())
    ];

    return {
        ssrKeysToRemove: ssrKeysToRemoveOnBundleChange.distinct(key => key),
        requests: sellJourneyBundleRequests
    }
}


export function createJourneyResellBundleRequests(journey: JourneyModel, bundleSsrsUpgradesPairs: IBundleSsrUpgradePair[])
    : {requests: IDotRezSellJourneyBundleRequest[]; ssrKeysToRemove: string[];} {


    const bundlesWithPassengersKeys = journey.getPassengerKeysGroupedByBundle() ?? {};

    if(Check.isEmpty(bundlesWithPassengersKeys)) {
        return {
            requests: [],
            ssrKeysToRemove: []
        };
    }

    const sellJourneyBundleRequests: IDotRezSellJourneyBundleRequest[] = [];
    let ssrKeysToRemoveOnBundleChange: string[] = [];

    for(let bundleCode of Object.keys(bundlesWithPassengersKeys)) {

        const journeyBundle = journey.allBundles.find(b => b.bundleCode === bundleCode);
        if(!journeyBundle) {
            continue;
        }

        const bundleSellRequest: IDotRezSellJourneyBundleRequest = {
            journeyKey: journey.journeyKey,
            bundleCode: bundleCode,
            passengerKeys: bundlesWithPassengersKeys[bundleCode]
        }
        sellJourneyBundleRequests.push(bundleSellRequest);

        if(isChildBundleCode(bundleCode)) {
            continue;
        }

        const journeyBundleSsrUpgrades = journeyBundle.getSsrsAvailableUpgrades(bundleSsrsUpgradesPairs);

        const upgradeBundleSsrsRequest = buildBundleSsrsUpgradeRequest(journeyBundleSsrUpgrades);
        if(upgradeBundleSsrsRequest) {
            bundleSellRequest.upgradeRequest = upgradeBundleSsrsRequest;
        }

        ssrKeysToRemoveOnBundleChange = [
            ...ssrKeysToRemoveOnBundleChange,
            ...journey.getSsrKeysToRemoveOnBundleChange(journeyBundle, journeyBundleSsrUpgrades),
            ...journeyBundleSsrUpgrades.selectMany(ssrUpgrade => ssrUpgrade.getSsrKeysToRemoveWhenUpgrading())
        ];

    }


    return {
        ssrKeysToRemove: ssrKeysToRemoveOnBundleChange.distinct(key => key),
        requests: sellJourneyBundleRequests
    }
}